import React, { type FC, useEffect, useRef } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Launcher from 'common/app/Launcher/Login';
import routes from './routes';
import loaders from './loaders';
import languages from 'common/i18n/utils/languages';
import { getTheme } from 'pc-sales/store/actions/system';
import './index.less';

interface LoginProps {
    companySetting: Record<string, unknown>;
    designSetting: Record<string, unknown>;
    // Login page logo now comes from the theme bundle (/v2/platform/setting/theme);
    // dispatch getTheme on mount so SignInLayout has mainLogo ready.
    // 登录页 logo 改为主题包（/v2/platform/setting/theme）的主 logo；
    // 进入页面时拉取主题，SignInLayout 即可拿到 mainLogo。
    getTheme: (payload: { mode: 'light' | 'dark' }) => void;
}

const Login: FC<LoginProps> = ({ companySetting, designSetting, getTheme }) => {
    // Keep the action reference stable so the useEffect below only fires once.
    // bindActionCreators returns a new wrapper on every render; without this guard
    // the dep array `[getTheme]` would re-trigger the effect on every parent re-render.
    // 锁定 action 引用，让下面的 useEffect 只触发一次。
    // bindActionCreators 每次渲染都会生成新的包装函数；如果直接把 getTheme 放进依赖数组，
    // 父组件每次重渲染都会再次触发 effect。
    const getThemeRef = useRef(getTheme);
    getThemeRef.current = getTheme;

    useEffect(() => {
        // Fetch light-mode theme (favicon / mainLogo / squareLogo / clientLoginBg).
        // Public endpoint — login page is unauthenticated.
        // 拉取浅色模式主题（favicon / mainLogo / squareLogo / clientLoginBg）。
        // 公开接口 — 登录页未登录即可访问。
        getThemeRef.current({ mode: 'light' });
    }, []);

    return (
        <Router>
            <Launcher
                lang={languages.getDefault()}
                company={companySetting}
                routesConfig={{
                    routes,
                    loaders,
                    presets: {
                        // logo / loginLogo are no longer the source — they used to come
                        // from PlatForm_Company and are now sourced from the theme bundle
                        // (/v2/platform/setting/theme) read by SignInLayout via Redux.
                        // / logo / loginLogo 已不再是 logo 来源 —— 它们以前从 PlatForm_Company
                        // 读取，现在由 SignInLayout 通过 Redux 读取主题包（/v2/platform/setting/theme）。
                        companyName: companySetting.company,
                        website: companySetting.website,
                    },
                }}
                name={designSetting?.style === 'center' ? 'definition-red' : ''}
            />
        </Router>
    );
};

export default connect(
    (state: Record<string, any>) => ({
        companySetting: state.system.getIn(['setting', 'PlatForm_Company']),
        designSetting: state.system.getIn(['setting', 'PlatForm_Design']),
    }),
    (dispatch) =>
        bindActionCreators(
            {
                getTheme: getTheme.init,
            },
            dispatch,
        ),
)(Login);
